'use client';

import { RefObject, useContext, useEffect, useRef } from 'react';

import { ProductTradeInContext } from '../../Context';
import { filterOptionsByBrand, filterVariantOptions } from '../../helpers';
import { Actions, MappedOption } from '../../types';
import { VariantSelector } from './VariantSelector';

const ProductSelector = ({ scrollRef }: { scrollRef: RefObject<HTMLDivElement | null> }) => {
  const firstLevel = 0;
  const {
    options,
    selectedBrand,
    variantChoices,
    selectedOption,
    updateSelectedVariant,
    dispatch,
    handleAnswerChange
  } = useContext(ProductTradeInContext);

  const previouslySelectedOption = useRef(selectedOption);
  const previousVariantChoices = useRef(variantChoices);

  useEffect(() => {
    if (selectedOption && selectedOption !== previouslySelectedOption?.current) {
      previouslySelectedOption.current = selectedOption;
      dispatch?.({ type: Actions.SET_VARIANT_CHOICES, payload: [String(selectedOption)] });
      updateSelectedVariant?.(null);
    }
  }, [dispatch, options, selectedBrand, selectedOption, updateSelectedVariant]);

  useEffect(() => {
    if (variantChoices && variantChoices === previousVariantChoices?.current) {
      return;
    }
    previousVariantChoices.current = variantChoices;
    const scrollToElement = scrollRef?.current?.querySelector(
      `[data-level="${variantChoices?.length}"]`
    );
    const position = scrollToElement?.getBoundingClientRect().top;
    scrollRef?.current?.scrollTo({ top: position, behavior: 'smooth' });
  }, [scrollRef, variantChoices]);

  if (!options) return null;

  const optionsFromSelectedBrand = filterOptionsByBrand(options, selectedBrand);
  const variantOptions = filterVariantOptions(optionsFromSelectedBrand, selectedOption);

  function handleProductLineChange(optionValue: string) {
    dispatch?.({ type: Actions.SET_SELECTED_OPTION, payload: optionValue });

    const productLineQuestion = optionsFromSelectedBrand?.[0].variances[0].title;

    if (productLineQuestion) {
      handleAnswerChange?.({ question: productLineQuestion }, optionValue);
    }
  }

  return (
    <>
      <VariantSelector
        options={optionsFromSelectedBrand}
        level={firstLevel}
        selected={selectedOption}
        onSelect={optionValue => handleProductLineChange(optionValue)}
      />

      {variantChoices?.map((choice, index) => {
        const currentLevel = index + 1;
        const filteredVariantOptions = variantOptions?.filter(option => {
          return variantChoices
            ?.slice(0, currentLevel)
            .every((_choice, i) => option.variances[i]?.value === variantChoices[i]);
        });

        function handleVariantSelection(choice: string) {
          if (!variantChoices) return;

          dispatch?.({
            type: Actions.SET_VARIANT_CHOICES,
            payload: variantChoices.slice(0, currentLevel).concat(choice)
          });

          const variantQuestion = filteredVariantOptions?.[0].variances[currentLevel].title;

          if (variantQuestion) {
            handleAnswerChange?.({ question: variantQuestion }, choice);
          }

          if (isLastVariance(filteredVariantOptions, index)) {
            const selectedOptionId = getSelectedOptionId(
              filteredVariantOptions,
              variantChoices,
              choice
            );

            if (selectedOptionId) {
              updateSelectedVariant?.(selectedOptionId);
            }
          } else {
            updateSelectedVariant?.(null);
          }
        }

        return (
          <VariantSelector
            key={choice + '_' + currentLevel}
            options={filteredVariantOptions}
            level={currentLevel}
            selected={variantChoices[currentLevel]}
            onSelect={selection => handleVariantSelection(selection)}
          />
        );
      })}
    </>
  );
};

function isLastVariance(options: MappedOption[] | undefined, index: number) {
  return options?.[0].variances.length === index + 2;
}

export function getSelectedOptionId(
  options: MappedOption[] | undefined,
  variantChoices: string[],
  choice: string
) {
  return options?.find(option => {
    const allVariantChoicesMatch = variantChoices.every((choice, index) => {
      return option.variances[index]?.value === choice;
    });

    return (
      allVariantChoicesMatch && option.variances[option.variances.length - 1]?.value === choice
    );
  })?.optionId;
}

export default ProductSelector;
